/**
 * The register wizard's controller: `register.model.ts` and the provenance
 * model wired to `fetchCollections`, `searchArtists`, and at submission the
 * three writes in order — `ensureWork`, `uploadWorkImages`,
 * `writeDraftProvenance`.
 *
 * It owns what the model cannot: the stage, the form values, the images chosen
 * on this device, the draft provenance items, the artist-search counter that
 * lets a stale answer be ignored, and the write ledger that stops Confirm
 * creating a second painting. The model decides what is valid and what each
 * step shows; `register.constants.ts` holds the words and the declarations.
 *
 * Nothing exists server-side until Confirm. An abandoned wizard leaves no work,
 * no rows and no objects in the bucket, and that is a property to keep.
 */

import type {
  ArtistMatch,
  ArtistSummary,
  CollectionSummary,
  ImageKind,
  ProvenanceDocumentKind,
} from "@ondo/shared";
import { useEffect, useRef, useState } from "react";

import { fetchCollections, matchArtists, searchArtists } from "../lib/api.ts";
import { ensureWork, newLedger, writeDraftProvenance } from "../lib/provenanceWrite.ts";
import { uploadWorkImages } from "../lib/workImages.ts";
import {
  addDocuments,
  addEntry,
  attachOndoId,
  describeProvenance,
  detachOndoId,
  firstInvalidEntryId,
  recordAct,
  removeAct,
  removeDocument,
  removeEntry,
  setDocumentKind,
  setEntryField,
  toEntryRows,
  toTimelineRows,
} from "../provenance/provenance.model.ts";
import type { ProvenanceItem } from "../provenance/provenance.model.ts";
import { MAX_ENTRIES } from "../provenance/provenance.constants.ts";
import type { ProvenanceHandlers } from "../provenance/ProvenanceEntryList.tsx";
import { PROVENANCE_SURFACES } from "../provenance/provenance.surfaces.ts";
import { useProvenanceSpotlight } from "../provenance/useProvenanceSpotlight.ts";
import type { SpotlightView } from "../spotlight/useSpotlight.ts";
import type { ProvenancePanelModel } from "./ProvenancePanel.tsx";
import { REGISTER_COPY } from "./register.constants.ts";
import type { DeclarationKey } from "./register.constants.ts";
import {
  EMPTY_ARTWORK,
  artistGate,
  confirmRows,
  declarationRows,
  describeCandidate,
  describeError,
  describeRouting,
  nextKind,
  registerTargets,
  screenImages,
  stepIndex,
  toRegisterRequest,
  validateStep,
  viewRows,
} from "./register.model.ts";
import type {
  ArtworkValues,
  DeclarationRow,
  PendingImage,
  Stage,
  SummaryRow,
  TargetOption,
  ViewRow,
} from "./register.model.ts";

export type RegisterView =
  | { kind: "loading" }
  /**
   * Nowhere to put it. You cannot register a work without a collection you may
   * curate, because the collection is what records who owns the work — a state
   * the prototype has no concept of, and one that falls straight out of the
   * placement model.
   */
  | { kind: "no-target"; message: string }
  | {
      kind: "form";
      /**
       * Never "registered": that stage has its own view variant, so the panel
       * switch cannot be handed a stage it has no panel for.
       */
      stage: Exclude<Stage["kind"], "registered">;
      step: number;
      values: ArtworkValues;
      targets: TargetOption[];
      artists: ArtistSummary[];
      routing: string;
      /**
       * OND-90. The confirmation step's own view-model, present only while it
       * is the stage — so the panel cannot render a list of candidates for a
       * name the field has moved past.
       */
      artistMatch: ArtistMatchView | null;
      rows: SummaryRow[];
      declarations: DeclarationRow[];
      /** Chosen on this device, uploaded at submission. */
      images: PendingImage[];
      views: ViewRow[];
      provenance: ProvenancePanelModel;
      /** Why files just dropped were refused. Cleared by the next drop. */
      refusals: string[];
      busy: boolean;
      error: string | null;
    }
  | { kind: "registered"; workId: string; title: string; message: string; notices: string[] };

/**
 * What the "did you mean one of these?" panel renders.
 *
 * Already decided, as every view-model here is: the candidate rows are strings
 * the model composed, so the panel carries no formatting and no business
 * condition. `candidates` empty is a state that cannot be reached — the gate
 * answers `not-on-ondo` instead and that sentence is shown on the details
 * panel, beside the field it is about.
 */
export interface ArtistMatchView {
  typedName: string;
  candidates: { id: string; label: string; exact: boolean }[];
}

export interface RegisterController {
  view: RegisterView;
  set: <K extends keyof ArtworkValues>(key: K, value: ArtworkValues[K]) => void;
  toggleDeclaration: (key: DeclarationKey) => void;
  chooseArtist: (artist: ArtistSummary | null) => void;
  searchFor: (query: string) => void;
  /**
   * OND-90. Accepts one of the candidates on the confirmation step.
   *
   * Takes the id and not the row, because the view holds only ids and labels —
   * which is the MVC split doing its job here rather than an inconvenience: a
   * panel handed the whole `ArtistMatch` could render `hasAuthority` or
   * `authorityRole`, and the one thing this screen may never do is say
   * anything about the body appointed over an artist.
   */
  confirmArtist: (artistId: string) => void;
  /** OND-90. "None of these" — records the decline and goes on. */
  declineArtists: () => void;
  addImages: (files: File[]) => void;
  removeImage: (id: string) => void;
  setImageKind: (id: string, kind: ImageKind) => void;
  toggleView: (kind: ImageKind) => void;
  provenance: ProvenanceHandlers;
  /** The document viewer, mounted once by the screen. */
  spotlight: SpotlightView;
  closeDocument: () => void;
  addProvenanceEntry: () => void;
  next: () => void;
  back: () => void;
  submit: () => void;
}

/**
 * The four-step registration.
 *
 * Structurally the same screen as signup, and named to match: a `Stage` union,
 * one `validateStep`, a `busy`/`error` wrapper around every call. What differs
 * is that the draft is server-side from the first step — `begin_registration`
 * opens a row so images have something to attach to before a title exists.
 */
export function useRegisterController(): RegisterController {
  const [stage, setStage] = useState<Stage>({ kind: "images" });
  /**
   * Which artist search the list on screen is answering.
   *
   * A counter and not an AbortController: the request is cheap and already in
   * flight, and all that matters is that a stale answer does not get to write.
   */
  const artistSearch = useRef(0);
  const [values, setValues] = useState<ArtworkValues>(EMPTY_ARTWORK);
  const [collections, setCollections] = useState<CollectionSummary[] | null>(null);
  const [artists, setArtists] = useState<ArtistSummary[]>([]);
  const [chosenArtist, setChosenArtist] = useState<ArtistSummary | null>(null);
  const [images, setImages] = useState<PendingImage[]>([]);
  const [extraViews, setExtraViews] = useState<ImageKind[]>([]);
  const [refusals, setRefusals] = useState<string[]>([]);
  const [items, setItems] = useState<ProvenanceItem[]>([]);
  const [openEntryId, setOpenEntryId] = useState<string | null>(null);
  const [docRefusals, setDocRefusals] = useState<
    { entryId: string; sentences: string[] } | null
  >(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** OND-90. True only while the candidate list is in flight. */
  const [matching, setMatching] = useState(false);
  /**
   * OND-90. The name the registrant was told is not on ONDO.
   *
   * THE NAME AND NOT A FLAG, so the sentence cannot be left standing over a
   * different name after the field is retyped — the lesson `BuyerCheck` in the
   * sell wizard records about a verdict and the id it was asked about.
   */
  const [notOnOndo, setNotOnOndo] = useState<string | null>(null);

  // Every object URL handed to a thumbnail, so unmounting mid-wizard does not
  // leave the blobs alive for the rest of the session. Removing one image
  // revokes its own URL immediately; this is only the abandonment case.
  const previewUrls = useRef<string[]>([]);

  /**
   * What has already been filed, kept across attempts.
   *
   * A ref rather than state, because the guard is worthless if it is rebuilt on
   * the render that follows a failure. Submit is a button a collector on a bad
   * connection presses twice, and every step behind it is one the database
   * refuses, duplicates or 409s on a second call.
   */
  const ledger = useRef(newLedger());

  const { view: spotlight, openDocument, closeDocument } = useProvenanceSpotlight(
    items,
    PROVENANCE_SURFACES.compose,
  );
  useEffect(
    () => () => {
      for (const url of previewUrls.current) URL.revokeObjectURL(url);
    },
    [],
  );

  useEffect(() => {
    let active = true;

    fetchCollections()
      .then((rows) => {
        if (!active) return;
        setCollections(rows);
        const targets = registerTargets(rows);
        // Pre-select when there is exactly one, so the common case needs no
        // decision. More than one and the collector must say which.
        if (targets.length === 1) {
          setValues((current) => ({ ...current, collectionId: targets[0]?.id ?? null }));
        }
      })
      .catch((cause: unknown) => {
        if (active) setError(describeError(cause));
      });

    return () => {
      active = false;
    };
  }, []);

  const run = async (action: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (cause: unknown) {
      setError(describeError(cause));
    } finally {
      setBusy(false);
    }
  };

  const next = () => {
    const problem = validateStep(stage.kind, values, items);
    if (problem) {
      setError(problem);
      // Open the entry that failed. A message naming "Entry 3" over a list of
      // collapsed rows is a message the reader has to go looking for.
      const offender = firstInvalidEntryId(items);
      if (offender) setOpenEntryId(offender);
      return;
    }
    setError(null);

    if (stage.kind === "images") setStage({ kind: "details" });
    // OND-90. LEAVING THE DETAILS STEP IS WHERE THE ARTIST IS SETTLED.
    //
    // Not on blur and not per keystroke, which were both considered and are
    // both worse. On blur, the registrant meets the question while still
    // editing the field and before they have said they are finished with it;
    // per keystroke it is `searchArtists` again, asking a question nobody has
    // yet committed to. Here it is a bank transfer's confirmation screen: one
    // ask, about a name they have declared finished by pressing Continue.
    else if (stage.kind === "details") void settleArtist();
    // NO BRANCH FOR "artist-match", AND THE ABSENCE IS THE FEATURE. The
    // question has exactly two answers — pick one, or say none of these — and
    // a Continue that walked past it would be the silent skip this whole
    // ticket is about, rebuilt one layer up. The view hides the button on that
    // stage; this is the half of that which a future edit cannot undo by
    // accident.
    else if (stage.kind === "provenance") setStage({ kind: "confirm" });
  };

  /**
   * Ask which artist the typed name means, then act on the answer.
   *
   * The gate itself is pure and lives in the model; this is only the fetch and
   * the four moves. A failure to reach the endpoint does NOT block the
   * registration: the work is the collector's to create and a confirmation step
   * that cannot be answered must not become a wall in front of it. It falls
   * through with no candidates, which the gate reads as `not-on-ondo` — the
   * honest thing to say when nothing could be checked, and the same state as a
   * name that genuinely matches nobody.
   */
  const settleArtist = async () => {
    const typedName = values.attributedName.trim();

    const gate = (candidates: ArtistMatch[]) =>
      artistGate({
        typedName,
        artistId: values.artistId,
        declinedFor: values.artistDeclinedFor,
        candidates,
      });

    // Asked BEFORE the fetch, so a registrant who picked from the suggestions —
    // or who has already declined for this exact name — is never made to wait
    // on a request whose answer cannot change anything.
    const settled = gate([]);
    if (settled.kind === "already-picked") {
      setStage({ kind: "provenance" });
      return;
    }

    setMatching(true);
    let candidates: ArtistMatch[];
    try {
      candidates = await matchArtists(typedName);
    } catch {
      candidates = [];
    } finally {
      setMatching(false);
    }

    const decision = gate(candidates);
    switch (decision.kind) {
      case "linked":
        // AN EXACT TYPED NAME LINKS WITHOUT A PROMPT, which is the ticket's
        // first assertion. The registrant typed the artist's name correctly;
        // making them click a list saying the same thing back is the friction a
        // confirmation step has to earn, and here it earns nothing.
        setChosenArtist(decision.artist);
        setValues((current) => ({ ...current, artistId: decision.artist.id }));
        setStage({ kind: "provenance" });
        return;
      case "confirm":
        setStage({
          kind: "artist-match",
          typedName: decision.typedName,
          candidates: decision.candidates,
        });
        return;
      case "not-on-ondo":
        // NO SILENT FAILURE. The decline is recorded against this exact name,
        // the plain sentence is shown on the field it is about, and the work
        // still goes forward — the registrant is told what it costs rather than
        // stopped.
        setValues((current) => ({ ...current, artistDeclinedFor: typedName }));
        setNotOnOndo(typedName);
        setStage({ kind: "provenance" });
        return;
      default:
        setStage({ kind: "provenance" });
        return;
    }
  };

  const back = () => {
    setError(null);
    if (stage.kind === "details") setStage({ kind: "images" });
    // Back out of the question onto the field it is about, so a registrant who
    // realises they misspelled the name can fix it rather than having to
    // decline a list of artists none of whom is the one they meant.
    else if (stage.kind === "artist-match") setStage({ kind: "details" });
    else if (stage.kind === "provenance") setStage({ kind: "details" });
    else if (stage.kind === "confirm") setStage({ kind: "provenance" });
  };

  const submit = () => {
    const problem = validateStep("confirm", values, items);
    if (problem) {
      setError(problem);
      return;
    }

    void run(async () => {
      const collectionId = values.collectionId;
      if (!collectionId) throw new Error(REGISTER_COPY.targetRequired);

      // THE WORK FIRST, because there is nothing for anything else to attach
      // to until it exists. This is the inversion the reversal makes: a draft
      // registration used to play this part, and a duplicate of one was
      // invisible. A duplicate WORK is a second painting in the catalogue, so
      // the ledger — not React state — is what stops Confirm making two.
      const workId = await ensureWork(collectionId, toRegisterRequest(values), ledger.current);

      // Then the bytes, then the chain of title. The images go first only
      // because a card with no photograph is the more visible half-done state;
      // both are guarded by the same ledger and neither is filed twice.
      if (images.length > 0) await uploadWorkImages(workId, images);
      const written = await writeDraftProvenance(items, workId, ledger.current);

      // A work that was created with one ask refused has to say so on the only
      // screen the collector sees next.
      setStage({
        kind: "registered",
        workId,
        title: values.title.trim(),
        notices: written.refusals,
      });
    });
  };

  const addImages = (files: File[]) => {
    const intake = screenImages(files, images);
    setRefusals(intake.rejected);

    if (intake.accepted.length === 0) return;

    setImages((current) => {
      // Folded rather than mapped: each file's presumed kind depends on the
      // ones before it in the same drop, so 'front' cannot be guessed twice.
      let next = current;
      for (const file of intake.accepted) {
        const url = URL.createObjectURL(file);
        previewUrls.current.push(url);
        next = [
          ...next,
          { id: crypto.randomUUID(), file, kind: nextKind(next), previewUrl: url },
        ];
      }
      return next;
    });
  };

  const removeImage = (id: string) => {
    setImages((current) => {
      const going = current.find((image) => image.id === id);
      if (going) URL.revokeObjectURL(going.previewUrl);
      return current.filter((image) => image.id !== id);
    });
  };

  return {
    view: resolve({
      stage,
      values,
      collections,
      artists,
      chosenArtist,
      images,
      extraViews,
      refusals,
      items,
      openEntryId,
      docRefusals,
      busy,
      matching,
      notOnOndo,
      error,
    }),
    set: (key, value) => setValues((current) => ({ ...current, [key]: value })),
    toggleDeclaration: (key) =>
      setValues((current) => ({
        ...current,
        declarations: { ...current.declarations, [key]: !current.declarations[key] },
      })),
    chooseArtist: (artist) => {
      setChosenArtist(artist);
      setValues((current) => ({
        ...current,
        artistId: artist?.id ?? null,
        attributedName: artist?.displayName ?? current.attributedName,
      }));
    },
    // OND-90. Accepting a candidate is the same act as picking a suggestion —
    // it goes through `chooseArtist`'s own effect on the values rather than a
    // second spelling of it — and then carries on out of the question.
    confirmArtist: (artistId) => {
      // Resolved out of the stage that asked the question, so a candidate can
      // only ever be accepted from the list it was offered in.
      const artist =
        stage.kind === "artist-match"
          ? stage.candidates.find((candidate) => candidate.id === artistId)
          : undefined;
      if (!artist) return;

      setChosenArtist(artist);
      setValues((current) => ({
        ...current,
        artistId: artist.id,
        // The typed words are replaced by the catalogue's spelling here, and
        // ONLY here. Picking from a list is choosing that artist's name as the
        // registry files it, which is what `chooseArtist` has always done; the
        // decline path below leaves the typed string untouched.
        attributedName: artist.displayName,
        artistDeclinedFor: null,
      }));
      setNotOnOndo(null);
      setStage({ kind: "provenance" });
    },
    // "None of these." The work is registered reading exactly what was typed,
    // and the registrant has been told in words that it will not reach that
    // artist's estate. Recorded against the NAME, so retyping asks again.
    declineArtists: () => {
      const typedName = values.attributedName.trim();
      setValues((current) => ({ ...current, artistDeclinedFor: typedName }));
      setNotOnOndo(typedName);
      setStage({ kind: "provenance" });
    },
    searchFor: (query) => {
      // NOT DEBOUNCED, deliberately, and the argument is written down in
      // `workPicker.constants.ts`: one request per keystroke behind a
      // two-character minimum is defensible for a typeahead returning at most
      // twenty names, where it is not for a paged endpoint that also counts.
      //
      // What that argument does not cover is ORDER. Requests go out per
      // keystroke and answers come back whenever they come back, so a slow
      // reply for "Erw" lands after a fast one for "Erwin Wurm" and replaces a
      // correct list with a stale one — the reader then picks an artist from
      // results for a query they have typed past. The ticket is the same guard
      // `WorkPickerDialog` uses, which has a test named for exactly this.
      const ticket = ++artistSearch.current;
      const land = (rows: ArtistSummary[]) => {
        if (artistSearch.current === ticket) setArtists(rows);
      };

      if (query.trim().length < 2) {
        land([]);
        return;
      }
      searchArtists(query)
        .then(land)
        .catch(() => {
          land([]);
        });
    },
    addImages,
    removeImage,
    setImageKind: (id, kind) =>
      setImages((current) =>
        current.map((image) => (image.id === id ? { ...image, kind } : image)),
      ),
    toggleView: (kind) =>
      setExtraViews((current) =>
        current.includes(kind)
          ? current.filter((view) => view !== kind)
          : [...current, kind],
      ),
    spotlight,
    closeDocument,
    // A new entry opens itself, which is the whole reason the open state is the
    // controller's business and not a <details> element's.
    addProvenanceEntry: () =>
      setItems((current) => {
        const next = addEntry(current);
        const added = next[next.length - 1];
        if (added && added.kind === "draft") setOpenEntryId(added.entry.id);
        return next;
      }),
    provenance: {
      onToggle: (id) => setOpenEntryId((current) => (current === id ? null : id)),
      onSetField: (id, key, value) =>
        setItems((current) => setEntryField(current, id, key, value)),
      onAddDocuments: (id, files) =>
        setItems((current) => {
          const intake = addDocuments(current, id, files);
          setDocRefusals(
            intake.rejected.length > 0 ? { entryId: id, sentences: intake.rejected } : null,
          );
          return intake.items;
        }),
      onSetDocumentKind: (id, documentId, kind: ProvenanceDocumentKind) =>
        setItems((current) => setDocumentKind(current, id, documentId, kind)),
      onOpenDocument: openDocument,
      // Unreachable here, and unreachable structurally rather than by luck:
      // every row in the wizard is a draft, and only a recorded one can carry a
      // withdraw control. Nothing in this browser has been filed, so there is
      // nothing to take back off the record.
      onWithdraw: () => undefined,
      // Unreachable for the same reason, one step further out: an attestation
      // is somebody's signature on a FILED entry, and nothing here is filed.
      // The confirmations panel that carries the control only renders on a
      // recorded row.
      onWithdrawAttestation: () => undefined,
      // And a comment is filed WITH the entry at submit, so before then there is
      // no recorded comment to withdraw either — a queued one is removed with
      // `onUnact`, which is the draft's own control.
      onWithdrawComment: () => undefined,
      onRemoveDocument: (id, documentId) =>
        setItems((current) => removeDocument(current, id, documentId)),
      // Returns the problem rather than storing it: nothing outside the field
      // that raised it can act on "that is not an ONDO id".
      onAttachOndoId: (id, raw) => {
        const attached = attachOndoId(items, id, raw);
        if (!attached.problem) setItems(attached.items);
        return attached.problem;
      },
      onDetachOndoId: (id) => setItems((current) => detachOndoId(current, id)),
      onRemove: (id) => setItems((current) => removeEntry(current, id)),
      // Recorded here, filed at submit. Returns the problem rather than storing
      // it, exactly as onAttachOndoId does — nothing outside the field that
      // raised it can act on "that is not an ONDO id".
      onAct: (id, intent) => {
        const recorded = recordAct(items, id, intent);
        if (!recorded.problem) setItems(recorded.items);
        return recorded.problem;
      },
      onUnact: (id, actId) => setItems((current) => removeAct(current, id, actId)),
      // UNREACHABLE ON THIS SURFACE, and that is the wizard's whole nature
      // rather than a gap. Every entry here is a draft and every document a
      // file on the device, so `documentVerdictFor` is never asked about one —
      // a draft's row carries `verdict: null` and the control does not render.
      // There is nothing on the registry for a verdict to be about until step
      // 3 is submitted, and the work record is where it is then given.
      onJudgeDocument: () => null,
      onUndoDocumentVerdict: () => undefined,
    },
    next,
    back,
    submit,
  };
}

function resolve(input: {
  stage: Stage;
  values: ArtworkValues;
  collections: CollectionSummary[] | null;
  artists: ArtistSummary[];
  chosenArtist: ArtistSummary | null;
  images: PendingImage[];
  extraViews: ImageKind[];
  refusals: string[];
  items: ProvenanceItem[];
  openEntryId: string | null;
  docRefusals: { entryId: string; sentences: string[] } | null;
  busy: boolean;
  matching: boolean;
  notOnOndo: string | null;
  error: string | null;
}): RegisterView {
  if (input.stage.kind === "registered") {
    return {
      kind: "registered",
      workId: input.stage.workId,
      title: input.stage.title,
      message: REGISTER_COPY.registered,
      notices: input.stage.notices,
    };
  }

  if (!input.collections) return { kind: "loading" };

  const targets = registerTargets(input.collections);
  if (targets.length === 0) {
    return { kind: "no-target", message: REGISTER_COPY.noTarget };
  }

  const targetName =
    targets.find((target) => target.id === input.values.collectionId)?.label ?? "";

  return {
    kind: "form",
    stage: input.stage.kind,
    step: stepIndex(input.stage),
    values: input.values,
    targets,
    artists: input.artists,
    // OND-90. The "not on ONDO" sentence outranks the routing note, because it
    // is the answer to a question the registrant has just been asked and the
    // routing note is a standing caption. It is shown only while it is about
    // the name actually in the field — a retyped name drops it, so the sentence
    // can never stand over a different artist.
    routing:
      input.notOnOndo !== null
      && input.notOnOndo === input.values.attributedName.trim()
      && input.values.artistId === null
        ? REGISTER_COPY.artistNotOnOndo
        : describeRouting(input.chosenArtist),
    rows: confirmRows(input.values, targetName, describeProvenance(input.items)),
    declarations: declarationRows(input.values),
    images: input.images,
    views: viewRows({
      signed: input.values.signed,
      extras: input.extraViews,
      images: input.images,
    }),
    refusals: input.refusals,
    provenance: {
      // The surface is named once, here. Everything the wizard shows
      // differently from the work record follows from that one word.
      rows: toEntryRows(input.items, PROVENANCE_SURFACES.compose),
      timeline: toTimelineRows(input.items),
      openEntryId: input.openEntryId,
      refusals: input.docRefusals,
      full: input.items.length >= MAX_ENTRIES,
    },
    artistMatch:
      input.stage.kind === "artist-match"
        ? {
            typedName: input.stage.typedName,
            candidates: input.stage.candidates.map((candidate) => ({
              id: candidate.id,
              label: describeCandidate(candidate),
              exact: candidate.exact,
            })),
          }
        : null,
    // The candidate request shares the wizard's one busy flag, so Continue and
    // Back are disabled while the question is being asked — a second press
    // would otherwise fire a second request and could land the stale answer.
    busy: input.busy || input.matching,
    error: input.error,
  };
}
